import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Box,
  Typography,
  Chip,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from '../utils/api';
import { INSTANT_FORMATS } from '../utils/dateUtils';
import { useFormatInstant } from '../hooks/useClinicTimezone';
import { executeUpdate, OfflineWriteBlockedError } from '../offline/executeWrite';
import { withOfflineFallback } from '../offline/offlineQuery';
import { readHorse } from '../offline/readers';
import { useAuth } from '../auth/useAuth';

interface Alert {
  id: string;
  text: string;
  isCritical: boolean;
  createdBy: string;
  createdAt: string;
}

interface AlertManagementDialogProps {
  open: boolean;
  onClose: () => void;
  horseId: string;
  horseName: string;
}

const AlertManagementDialog: React.FC<AlertManagementDialogProps> = ({
  open,
  onClose,
  horseId,
  horseName,
}) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // An alert's createdAt is an INSTANT — see the render site near the bottom.
  const formatInstant = useFormatInstant();
  const [editingAlertId, setEditingAlertId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    text: '',
    isCritical: false,
  });
  const [charCount, setCharCount] = useState(0);

  // Fetch active alerts. CRITICAL: the offline fallback must read the alert
  // array from the replicated Horse row — the create/update/delete mutations
  // below derive the NEW medicalAlerts array from this `alerts` value, and that
  // array fully OVERWRITES the horse's medicalAlerts on push. Without the
  // fallback, offline this query fails → `alerts` is [] → an offline edit would
  // wipe every existing safety alert. `medicalAlerts` is the same array the REST
  // GET returns, so the replica fallback is faithful.
  const { data: alerts = [], isLoading } = useQuery<Alert[]>(
    ['alerts', horseId],
    withOfflineFallback(
      async () => {
        const response = await api.get(`/horses/${horseId}/alerts`);
        return response.data;
      },
      async () => ((await readHorse(horseId))?.medicalAlerts ?? []) as Alert[],
    ),
    { enabled: open }
  );

  // Create alert mutation.
  // Safety alerts are not a standalone table — they live as the JSON array
  // `medicalAlerts` on the (already-replicated) Horse row. So an offline create
  // is expressed as an UPDATE to that array: we compute the new array from the
  // current alerts and route through executeUpdate against the horse. Online (or
  // flag-off) executeUpdate calls networkFn directly, so the dedicated alert
  // endpoint runs exactly as before (zero behavior change).
  const createAlert = useMutation(
    async (data: { text: string; isCritical: boolean }) => {
      const newAlert: Alert = {
        id: crypto.randomUUID(),
        text: data.text,
        isCritical: data.isCritical,
        createdBy: user?.uid ?? '',
        createdAt: new Date().toISOString(),
      };
      const newMedicalAlerts = [...alerts, newAlert];
      return executeUpdate<Alert>({
        entityType: 'horse',
        table: 'horses',
        entityId: horseId,
        payload: { medicalAlerts: newMedicalAlerts },
        networkFn: () =>
          api.post(`/horses/${horseId}/alerts`, data).then((r) => r.data),
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['alerts', horseId]);
        queryClient.invalidateQueries(['horses']);
        queryClient.invalidateQueries(['horse', horseId]);
        resetForm();
      },
      onError: (err) => {
        if (err instanceof OfflineWriteBlockedError) window.alert(err.message);
      },
    }
  );

  // Update alert mutation. Replace the target alert by id within the horse's
  // medicalAlerts array, then route the new array through executeUpdate (see
  // createAlert for why). networkFn keeps the original alert endpoint.
  const updateAlert = useMutation(
    async ({ alertId, data }: { alertId: string; data: Partial<Alert> }) => {
      const newMedicalAlerts = alerts.map((a) =>
        a.id === alertId
          ? {
              ...a,
              ...(data.text !== undefined && { text: data.text }),
              ...(data.isCritical !== undefined && { isCritical: data.isCritical }),
            }
          : a
      );
      return executeUpdate<Alert>({
        entityType: 'horse',
        table: 'horses',
        entityId: horseId,
        payload: { medicalAlerts: newMedicalAlerts },
        networkFn: () =>
          api.put(`/horses/${horseId}/alerts/${alertId}`, data).then((r) => r.data),
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['alerts', horseId]);
        queryClient.invalidateQueries(['horses']);
        queryClient.invalidateQueries(['horse', horseId]);
        resetForm();
        setEditingAlertId(null);
      },
      onError: (err) => {
        if (err instanceof OfflineWriteBlockedError) window.alert(err.message);
      },
    }
  );

  // Delete alert mutation. Remove the target alert by id from the horse's
  // medicalAlerts array, then route the new array through executeUpdate (see
  // createAlert for why). networkFn keeps the original alert endpoint.
  const deleteAlert = useMutation(
    async (alertId: string) => {
      const newMedicalAlerts = alerts.filter((a) => a.id !== alertId);
      return executeUpdate<void>({
        entityType: 'horse',
        table: 'horses',
        entityId: horseId,
        payload: { medicalAlerts: newMedicalAlerts },
        networkFn: () =>
          api.delete(`/horses/${horseId}/alerts/${alertId}`).then((r) => r.data),
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['alerts', horseId]);
        queryClient.invalidateQueries(['horses']);
        queryClient.invalidateQueries(['horse', horseId]);
      },
      onError: (err) => {
        if (err instanceof OfflineWriteBlockedError) window.alert(err.message);
      },
    }
  );

  const resetForm = () => {
    setFormData({ text: '', isCritical: false });
    setCharCount(0);
    setEditingAlertId(null);
  };

  const handleSubmit = () => {
    if (!formData.text.trim() || formData.text.length > 200) return;

    if (editingAlertId) {
      updateAlert.mutate({ alertId: editingAlertId, data: formData });
    } else {
      createAlert.mutate(formData);
    }
  };

  const handleEdit = (alert: Alert) => {
    setEditingAlertId(alert.id);
    setFormData({
      text: alert.text,
      isCritical: alert.isCritical,
    });
    setCharCount(alert.text.length);
  };

  const handleDelete = (alertId: string) => {
    if (window.confirm('Are you sure you want to delete this alert?')) {
      deleteAlert.mutate(alertId);
    }
  };

  const getAlertColor = (isCritical: boolean) => {
    return isCritical ? 'error' : 'warning';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Manage Alerts - {horseName}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Add critical information that must be visible before treatment.
          </Typography>
        </Box>

        {/* Alert Form */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            {editingAlertId ? 'Edit Alert' : 'Add New Alert'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Alert Text"
              value={formData.text}
              onChange={(e) => {
                const text = e.target.value;
                if (text.length <= 200) {
                  setFormData({ ...formData, text });
                  setCharCount(text.length);
                }
              }}
              placeholder="e.g., Requires Banamine 30 min prior to vaccines - severe reaction history"
              helperText={`${charCount}/200 characters`}
              error={charCount > 200}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isCritical}
                  onChange={(e) =>
                    setFormData({ ...formData, isCritical: e.target.checked })
                  }
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <WarningIcon fontSize="small" color="error" />
                  <Typography variant="body2">
                    Mark as Critical (high-contrast red styling)
                  </Typography>
                </Box>
              }
            />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!formData.text.trim() || charCount > 200}
              >
                {editingAlertId ? 'Update Alert' : 'Add Alert'}
              </Button>
              {editingAlertId && (
                <Button variant="outlined" onClick={resetForm}>
                  Cancel
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/* Active Alerts List */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Active Alerts ({alerts.length})
          </Typography>
          {alerts.length === 0 ? (
            <Alert severity="info">No active alerts for this patient.</Alert>
          ) : (
            <List>
              {alerts.map((alert) => (
                <ListItem
                  key={alert.id}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                  bgcolor: alert.isCritical ? 'error.lighter' : 'background.paper',
                }}
                secondaryAction={
                  <Box>
                    <Tooltip title="Edit">
                      <IconButton edge="end" onClick={() => handleEdit(alert)} sx={{ mr: 1 }}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton edge="end" onClick={() => handleDelete(alert.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flex: 1 }}>
                  <WarningIcon color={alert.isCritical ? 'error' : 'warning'} />
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                      {alert.isCritical && (
                        <Chip
                          label="CRITICAL"
                          size="small"
                          color="error"
                          icon={<WarningIcon />}
                        />
                      )}
                    </Box>
                    {/* INSTANT. A medical alert is not a schema row — it is an element of
                        the `Horse.medicalAlerts` JSON array — so its kind comes from the
                        two writers rather than a column: `alerts.service.ts:44` stamps
                        `createdAt: new Date().toISOString()` server-side, and the offline
                        create above does the same client-side. Both are true moments, which
                        is why DATE-KINDS.md §2.3 files alert `createdAt` with the instants.
                        The clinic's zone, not the viewer's — a relief vet in California
                        reading an East-coast clinic's 9 PM alert should see the day the
                        clinic recorded it. NOT a calendar day: date-parting this would push
                        an evening alert onto the following day. */}
                    <ListItemText
                      primary={alert.text}
                      secondary={`Added ${formatInstant(alert.createdAt, INSTANT_FORMATS.date)}`}
                    />
                  </Box>
                </Box>
              </ListItem>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlertManagementDialog;
